import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  HandCoins, Euro, Clock, CheckCircle, Calendar, Download, FileText,
  TrendingUp, ArrowUpRight, Wallet, Loader2, AlertCircle, BarChart3,
  ChevronLeft, ChevronRight, FileSpreadsheet
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, AreaChart, Area } from 'recharts';

const AffiliateCommissionsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState({
    total_earned: 0,
    pending_commission: 0,
    converted_referrals: 0
  });
  
  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [activeTab, setActiveTab] = useState('overview');
  
  // Convert modal state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertAmount, setConvertAmount] = useState('');
  const [converting, setConverting] = useState(false);
  
  // Preferred rate for conversion
  const PREFERRED_RATE = 90; // €/hour
  const NORMAL_RATE = 120; // €/hour

  // Available years for filter
  const availableYears = useMemo(() => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    years.add(currentYear.toString());
    years.add((currentYear - 1).toString());
    commissions.forEach(c => {
      if (c.date) {
        years.add(new Date(c.date).getFullYear().toString());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [commissions]);

  // Monthly data for charts
  const monthlyData = useMemo(() => {
    const months = [
      'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
      'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
    ];
    
    const data = months.map((month, index) => ({
      month,
      monthIndex: index,
      paid: 0,
      pending: 0,
      converted: 0,
      total: 0,
      count: 0
    }));
    
    commissions.forEach(commission => {
      if (!commission.date) return;
      const date = new Date(commission.date);
      if (date.getFullYear().toString() !== selectedYear) return;
      
      const monthIndex = date.getMonth();
      const amount = commission.amount || 0;
      
      data[monthIndex].count += 1;
      data[monthIndex].total += amount;
      
      if (commission.status === 'paid') {
        data[monthIndex].paid += amount;
      } else if (commission.status === 'converted') {
        data[monthIndex].converted += amount;
      } else {
        data[monthIndex].pending += amount;
      }
    });
    
    // Calculate cumulative totals
    let cumulative = 0;
    data.forEach(d => {
      cumulative += d.total;
      d.cumulative = cumulative;
    });
    
    return data;
  }, [commissions, selectedYear]);

  // Monthly summary table data
  const monthlySummary = useMemo(() => {
    return monthlyData.filter(m => m.total > 0 || m.count > 0);
  }, [monthlyData]);

  // Yearly totals
  const yearlyTotals = useMemo(() => {
    return monthlyData.reduce((acc, m) => ({
      paid: acc.paid + m.paid,
      pending: acc.pending + m.pending,
      converted: acc.converted + m.converted,
      total: acc.total + m.total,
      count: acc.count + m.count
    }), { paid: 0, pending: 0, converted: 0, total: 0, count: 0 });
  }, [monthlyData]);

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/affiliates/my-commissions');
      setCommissions(response.data.commissions || []);
      setStats({
        total_earned: response.data.total_earned || 0,
        pending_commission: response.data.pending_commission || 0,
        converted_referrals: response.data.converted_referrals || 0
      });
    } catch (error) {
      console.error('Error fetching commissions:', error);
      // Use demo data as fallback
      const demoCommissions = [
        { id: 1, client: 'TechStart SAS', project: 'Refonte site web', amount: 800, status: 'paid', date: '2026-01-15', paidDate: '2026-01-20' },
        { id: 2, client: 'Design Studio', project: 'Application mobile', amount: 450, status: 'pending', date: '2026-01-10', paidDate: null },
        { id: 3, client: 'BioFood SARL', project: 'Identité visuelle', amount: 650, status: 'paid', date: '2025-12-05', paidDate: '2025-12-15' },
        { id: 4, client: 'MediPlus', project: 'Site e-commerce', amount: 1200, status: 'paid', date: '2025-11-20', paidDate: '2025-11-30' },
        { id: 5, client: 'SportFit', project: 'Campagne social media', amount: 300, status: 'converted', date: '2025-10-08', paidDate: null },
        { id: 6, client: 'EcoTech', project: 'Brochure commerciale', amount: 250, status: 'paid', date: '2025-09-15', paidDate: '2025-09-25' },
      ];
      setCommissions(demoCommissions);
      setStats({
        total_earned: 3650,
        pending_commission: 450,
        converted_referrals: 6
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    const amount = parseFloat(convertAmount);
    if (!amount || amount <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }
    if (amount > stats.pending_commission) {
      toast.error('Le montant ne peut pas dépasser vos commissions en attente');
      return;
    }

    setConverting(true);
    try {
      await api.post('/affiliates/convert-to-service', {
        amount: amount,
        rate: PREFERRED_RATE
      });
      
      const hours = amount / PREFERRED_RATE;
      toast.success(`${hours.toFixed(1)} heures de service ajoutées à votre compte !`);
      setShowConvertModal(false);
      setConvertAmount('');
      fetchCommissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setConverting(false);
    }
  };

  // Export CSV
  const exportCSV = () => {
    const headers = ['Mois', 'Nombre de commissions', 'Payées (€)', 'En attente (€)', 'Converties (€)', 'Total (€)'];
    const rows = monthlySummary.map(m => [
      m.month,
      m.count,
      m.paid.toFixed(2),
      m.pending.toFixed(2),
      m.converted.toFixed(2),
      m.total.toFixed(2)
    ]);
    
    // Add totals row
    rows.push([
      'TOTAL',
      yearlyTotals.count,
      yearlyTotals.paid.toFixed(2),
      yearlyTotals.pending.toFixed(2),
      yearlyTotals.converted.toFixed(2),
      yearlyTotals.total.toFixed(2)
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `commissions_${selectedYear}.csv`;
    link.click();
    toast.success('Export CSV téléchargé');
  };

  // Export PDF (simple HTML print)
  const exportPDF = () => {
    const printWindow = window.open('', '_blank');
    const monthsHtml = monthlySummary.map(m => `
      <tr>
        <td style="padding: 8px; border: 1px solid #ddd;">${m.month}</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${m.count}</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${m.paid.toLocaleString('fr-FR')}€</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${m.pending.toLocaleString('fr-FR')}€</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${m.converted.toLocaleString('fr-FR')}€</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: right; font-weight: bold;">${m.total.toLocaleString('fr-FR')}€</td>
      </tr>
    `).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rapport des commissions ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #1e3a5f; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1e3a5f; color: white; padding: 10px; text-align: left; }
          .totals { background: #f0f9ff; font-weight: bold; }
          .summary { display: flex; gap: 20px; margin-bottom: 30px; }
          .summary-card { flex: 1; padding: 15px; border-radius: 8px; background: #f8f9fa; }
          .summary-card h3 { margin: 0 0 5px 0; font-size: 14px; color: #666; }
          .summary-card p { margin: 0; font-size: 24px; font-weight: bold; color: #1e3a5f; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <h1>📊 Rapport des Commissions</h1>
        <p class="subtitle">Année ${selectedYear} • ${user?.first_name} ${user?.last_name}</p>
        
        <div class="summary">
          <div class="summary-card">
            <h3>Total gagné</h3>
            <p>${yearlyTotals.paid.toLocaleString('fr-FR')}€</p>
          </div>
          <div class="summary-card">
            <h3>En attente</h3>
            <p>${yearlyTotals.pending.toLocaleString('fr-FR')}€</p>
          </div>
          <div class="summary-card">
            <h3>Clients convertis</h3>
            <p>${yearlyTotals.count}</p>
          </div>
        </div>
        
        <h2>Détail mensuel</h2>
        <table>
          <thead>
            <tr>
              <th>Mois</th>
              <th style="text-align: center;">Commissions</th>
              <th style="text-align: right;">Payées</th>
              <th style="text-align: right;">En attente</th>
              <th style="text-align: right;">Converties</th>
              <th style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${monthsHtml}
            <tr class="totals">
              <td style="padding: 10px; border: 1px solid #ddd;"><strong>TOTAL ${selectedYear}</strong></td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: center;">${yearlyTotals.count}</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${yearlyTotals.paid.toLocaleString('fr-FR')}€</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${yearlyTotals.pending.toLocaleString('fr-FR')}€</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${yearlyTotals.converted.toLocaleString('fr-FR')}€</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${yearlyTotals.total.toLocaleString('fr-FR')}€</td>
            </tr>
          </tbody>
        </table>
        
        <div class="footer">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          <p>Programme Apporteur d'Affaires • Commission : 10% du premier projet</p>
        </div>
        
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Rapport PDF généré');
  };

  const calculatedHours = convertAmount ? (parseFloat(convertAmount) / PREFERRED_RATE).toFixed(1) : 0;

  const statusConfig = {
    'pending': { color: 'bg-amber-100 text-amber-700', label: 'En attente', icon: Clock },
    'paid': { color: 'bg-emerald-100 text-emerald-700', label: 'Payée', icon: CheckCircle },
    'converted': { color: 'bg-purple-100 text-purple-700', label: 'Convertie', icon: Wallet },
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border">
        <p className="font-semibold mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString('fr-FR')}€
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Mes Commissions</h1>
            <p className="text-slate-500">Suivez vos gains et paiements</p>
          </div>
          
          {/* Year selector and exports */}
          <div className="flex items-center gap-2">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="sm" onClick={exportCSV} className="hidden sm:flex">
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF} className="hidden sm:flex">
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-green-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total {selectedYear}</p>
                  <p className="text-3xl font-bold">{yearlyTotals.total.toLocaleString('fr-FR')}€</p>
                </div>
                <Euro className="w-10 h-10 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Payées</p>
                  <p className="text-3xl font-bold text-emerald-600">{yearlyTotals.paid.toLocaleString('fr-FR')}€</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">En attente</p>
                  <p className="text-3xl font-bold text-amber-600">{yearlyTotals.pending.toLocaleString('fr-FR')}€</p>
                </div>
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Commissions</p>
                  <p className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>{yearlyTotals.count}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="monthly" className="gap-2">
              <Calendar className="w-4 h-4" />
              Détail mensuel
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <HandCoins className="w-4 h-4" />
              Historique
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab - Charts */}
          <TabsContent value="overview" className="space-y-6">
            {/* Monthly Evolution Chart */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  Évolution mensuelle {selectedYear}
                </CardTitle>
                <CardDescription>Répartition des commissions par statut</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="paid" name="Payées" fill="#10b981" stackId="a" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pending" name="En attente" fill="#f59e0b" stackId="a" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="converted" name="Converties" fill="#8b5cf6" stackId="a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Cumulative Chart */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  Évolution cumulative
                </CardTitle>
                <CardDescription>Total des commissions cumulées au fil des mois</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={monthlyData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="cumulative" 
                        name="Total cumulé" 
                        stroke={theme.colors?.accent || '#0ea5e9'} 
                        fill={theme.colors?.accent || '#0ea5e9'} 
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Detail Tab */}
          <TabsContent value="monthly">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Récapitulatif mensuel {selectedYear}</CardTitle>
                  <CardDescription>Détail des commissions par mois</CardDescription>
                </div>
                <div className="flex gap-2 sm:hidden">
                  <Button variant="outline" size="sm" onClick={exportCSV}>
                    <FileSpreadsheet className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportPDF}>
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : monthlySummary.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucune commission pour {selectedYear}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Mois</th>
                          <th className="text-center py-3 px-4 font-medium text-slate-600">Commissions</th>
                          <th className="text-right py-3 px-4 font-medium text-emerald-600">Payées</th>
                          <th className="text-right py-3 px-4 font-medium text-amber-600">En attente</th>
                          <th className="text-right py-3 px-4 font-medium text-purple-600">Converties</th>
                          <th className="text-right py-3 px-4 font-medium text-slate-800">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlySummary.map((month, index) => (
                          <tr key={index} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{month.month}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant="outline">{month.count}</Badge>
                            </td>
                            <td className="py-3 px-4 text-right text-emerald-600">
                              {month.paid > 0 ? `${month.paid.toLocaleString('fr-FR')}€` : '-'}
                            </td>
                            <td className="py-3 px-4 text-right text-amber-600">
                              {month.pending > 0 ? `${month.pending.toLocaleString('fr-FR')}€` : '-'}
                            </td>
                            <td className="py-3 px-4 text-right text-purple-600">
                              {month.converted > 0 ? `${month.converted.toLocaleString('fr-FR')}€` : '-'}
                            </td>
                            <td className="py-3 px-4 text-right font-bold">
                              {month.total.toLocaleString('fr-FR')}€
                            </td>
                          </tr>
                        ))}
                        {/* Totals row */}
                        <tr className="bg-slate-100 font-bold">
                          <td className="py-3 px-4">Total {selectedYear}</td>
                          <td className="py-3 px-4 text-center">
                            <Badge>{yearlyTotals.count}</Badge>
                          </td>
                          <td className="py-3 px-4 text-right text-emerald-600">{yearlyTotals.paid.toLocaleString('fr-FR')}€</td>
                          <td className="py-3 px-4 text-right text-amber-600">{yearlyTotals.pending.toLocaleString('fr-FR')}€</td>
                          <td className="py-3 px-4 text-right text-purple-600">{yearlyTotals.converted.toLocaleString('fr-FR')}€</td>
                          <td className="py-3 px-4 text-right text-lg">{yearlyTotals.total.toLocaleString('fr-FR')}€</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab - Full list */}
          <TabsContent value="history">
            {/* Convert to Budget Card */}
            {stats.pending_commission > 0 && (
              <Card className="mb-6 border-0 shadow-sm border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-6 h-6 text-purple-500" />
                      <div>
                        <p className="font-medium" style={{ color: theme.colors?.primary }}>Convertir en budget communication</p>
                        <p className="text-sm text-slate-500">Tarif préférentiel : {PREFERRED_RATE}€ HT/heure (au lieu de {NORMAL_RATE}€)</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      className="border-purple-500 text-purple-600 hover:bg-purple-50"
                      onClick={() => setShowConvertModal(true)}
                    >
                      <ArrowUpRight className="w-4 h-4 mr-1" />
                      Convertir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Commissions List */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Historique complet</CardTitle>
                <CardDescription>Toutes vos commissions</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : commissions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <HandCoins className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucune commission pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {commissions.map((commission) => {
                      const config = statusConfig[commission.status] || statusConfig.pending;
                      const StatusIcon = config.icon;
                      return (
                        <div key={commission.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className={cn("p-2 rounded-full", commission.status === 'paid' ? 'bg-emerald-100' : commission.status === 'converted' ? 'bg-purple-100' : 'bg-amber-100')}>
                              <StatusIcon className={cn("w-5 h-5", commission.status === 'paid' ? 'text-emerald-600' : commission.status === 'converted' ? 'text-purple-600' : 'text-amber-600')} />
                            </div>
                            <div>
                              <p className="font-medium" style={{ color: theme.colors?.primary }}>{commission.client}</p>
                              <p className="text-sm text-slate-500">{commission.project}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-lg" style={{ color: theme.colors?.primary }}>+{commission.amount}€</p>
                              <p className="text-xs text-slate-400">
                                {commission.status === 'paid' ? `Payée le ${commission.paidDate}` : commission.status === 'converted' ? 'Convertie en heures' : `Créée le ${commission.date}`}
                              </p>
                            </div>
                            <Badge className={config.color}>{config.label}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Convert Modal */}
        <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-purple-500" />
                Convertir en budget communication
              </DialogTitle>
              <DialogDescription>
                Convertissez vos commissions en heures de service à un tarif préférentiel
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Commissions disponibles</span>
                  <span className="font-bold text-purple-700">{stats.pending_commission}€</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Tarif préférentiel</span>
                  <span className="font-medium text-purple-700">{PREFERRED_RATE}€/h <span className="text-xs text-slate-400 line-through ml-1">{NORMAL_RATE}€/h</span></span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Montant à convertir (€)</Label>
                <Input
                  type="number"
                  value={convertAmount}
                  onChange={(e) => setConvertAmount(e.target.value)}
                  placeholder="Ex: 450"
                  max={stats.pending_commission}
                />
                {convertAmount && parseFloat(convertAmount) > stats.pending_commission && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Le montant ne peut pas dépasser {stats.pending_commission}€
                  </p>
                )}
              </div>

              {convertAmount && parseFloat(convertAmount) > 0 && parseFloat(convertAmount) <= stats.pending_commission && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                  <p className="text-sm text-slate-600 mb-1">Vous recevrez</p>
                  <p className="text-2xl font-bold text-emerald-700">{calculatedHours} heures</p>
                  <p className="text-xs text-slate-500">de budget communication</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleConvert}
                disabled={converting || !convertAmount || parseFloat(convertAmount) <= 0 || parseFloat(convertAmount) > stats.pending_commission}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {converting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Conversion...
                  </>
                ) : (
                  <>
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    Convertir
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AffiliateCommissionsPage;
